import React from 'react'
import { Link as RouterLink, useLocation } from 'react-router-dom'

import cx from 'classnames'
import { toNumber } from 'lodash'

import { EndUser } from '@driftt/api-types'
import { Button, Link } from '@driftt/dds'
import { Avatar, Div, H1, H5, ToolTip } from '@driftt/tide-core'

import { AccountActivityUserAvatar } from 'components/Avatars'
import Breadcrumb from 'components/Breadcrumb'
import { LocationState } from 'hooks/useAccountCompanyTitle'
import usePresenceSub from 'hooks/usePresenceSubscription'
import { accountPath, visitorUrl } from 'utils/paths'

import { ReactComponent as LightningBolt } from '../../../svgs/lightning-bolt-large.svg'
import { ReactComponent as LinkedInIcon } from '../../../svgs/linkedin.svg'
import { ReactComponent as SalesforceIcon } from '../../../svgs/salesforce.svg'
import { segmentTrack, TrackType } from '../../../utils/tracking'

import styles from './styles.module.css'

interface Props {
  activeTab: string
  tabNames: string[]
  unifiedAttributes: any
  onTabSelected: (selectedTab: string) => void
  contact: EndUser
  personalIntel: any
  companyIntel: any
  salesforceData: any
  accountData: any
}

const LINKEDIN_URL = 'https://www.linkedin.com/'

const getEndUserName = (user: EndUser, unifiedAttributes: any): string =>
  unifiedAttributes?.name?.value || user?.name || user?.email || 'Anonymous Site Visitor'

const GMAIL_BASE_URL = 'https://mail.google.com/mail/?view=cm&fs=1&to='

const InternalLink = React.forwardRef(({ navigate, children, ...props }: any, ref) => {
  return (
    <Link ref={ref} variant="inline" {...props}>
      {children}
    </Link>
  )
})

const ContactDetailsHeader = (props: Props) => {
  const location = useLocation<LocationState>()
  const {
    contact,
    unifiedAttributes,
    personalIntel,
    companyIntel,
    salesforceData,
    accountData
  } = props

  const presence = usePresenceSub(contact.orgId, [contact])
  const isOnline = !!presence?.has(toNumber(contact?.id))
  const lead = salesforceData?.leads ? salesforceData?.leads[0] : {}
  const title = personalIntel?.employmentTitle || contact?.attributes?.employment_title
  const company =
    personalIntel?.employmentName || contact?.attributes?.employment_name || companyIntel?.name

  const navigation = {
    title: location.state?.navigatedFrom,
    link: location.state?.navigatedFromUrl
  }

  const handleChatMessage = () => {
    segmentTrack(TrackType.CONTACT_DETAILS_CHAT_MESSAGE)
    window.open(visitorUrl(contact?.id))
  }

  const handleSendEmail = () => {
    if (contact?.email) {
      segmentTrack(TrackType.CONTACT_DETAILS_SEND_EMAIL)
      const mailToLink = `${GMAIL_BASE_URL}${contact?.email}`
      window.open(mailToLink)
    }
  }

  return (
    <Div className={styles.headerContainer}>
      <Div className={styles.headerSplash}>
        <section className={styles.navigationContainer}>
          <Breadcrumb to={{ title: contact?.name ?? 'Contact details' }} from={navigation} />
        </section>
        <LightningBolt />
      </Div>
      <Div className={styles.contactInfoContainer}>
        <Div className={styles.leftSide}>
          <AccountActivityUserAvatar
            size="x-large"
            className={styles.avatar}
            imageUrl={contact.avatarUrl}
            name={contact.name || contact.email}
            status={isOnline ? 'available' : 'away'}
          />
          <Div className={styles.contactInfo}>
            <H1 className={styles.contactName}>{getEndUserName(contact, unifiedAttributes)}</H1>
            {company && (
              <Div>
                {title?.length > 0 ? title : 'Works'} at{' '}
                <RouterLink to={accountPath(accountData?.accountId)} component={InternalLink}>
                  {company}
                </RouterLink>
              </Div>
            )}
            <Div className={styles.socialIcons}>
              {personalIntel?.linkedinHandle && (
                <ToolTip flow="up" component="LinkedIn" className={styles.tooltipWrapper}>
                  <a
                    href={`${LINKEDIN_URL}${personalIntel?.linkedinHandle}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.icon}>
                    <LinkedInIcon />
                  </a>
                </ToolTip>
              )}
              {salesforceData?.baseUrl && lead?.Id && (
                <ToolTip flow="up" component="Salesforce" className={styles.tooltipWrapper}>
                  <a
                    href={`${salesforceData.baseUrl}lightning/r/Lead/${lead.Id}/view`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.icon}>
                    <SalesforceIcon />
                  </a>
                </ToolTip>
              )}
            </Div>
          </Div>
        </Div>
        <Div className={styles.rightSide}>
          <Avatar type="company" company={companyIntel || {}} className={styles.companyLogo} />
          <Div className={styles.companyName}>
            <RouterLink
              className={styles.linkAction}
              to={accountPath(accountData?.accountId)}
              component={InternalLink}>
              {company}
            </RouterLink>
          </Div>
        </Div>
      </Div>
      <Div className={styles.tabsAndActions}>
        <Div className={styles.tabs}>
          {props.tabNames.map(tabName => (
            <H5
              key={tabName}
              className={cx(styles.tab, { [styles.activeTab]: props.activeTab === tabName })}
              onClick={() => props.onTabSelected(tabName)}>
              {tabName}
            </H5>
          ))}
        </Div>
        <Div className={styles.actionButtons}>
          {contact?.email && (
            <Button
              onClick={handleSendEmail}
              css={{
                marginRight: '$1'
              }}
              variant="tertiary">
              Send email
            </Button>
          )}
          <Button onClick={handleChatMessage}>Send chat message</Button>
        </Div>
      </Div>
    </Div>
  )
}

export default ContactDetailsHeader
